'use client';

import { useRef } from 'react';
import {
  MapPin,
  Clock,
  BookOpen,
  Lightbulb,
  List,
  ChevronRight,
  ExternalLink,
  Play,
  NotebookPen,
  Info,
  Radio,
  BookOpenText,
  Baby,
  Hand,
  Coffee,
  Droplet,
  CalendarCheck,
  type LucideIcon,
} from 'lucide-react';
import { ScrollIndicator } from '@church/nextjs-ui/components/ScrollIndicator';
import type { Sermon, ServiceTime } from '@/types/serve-finder';
import { Img } from '@/lib/asset-url';

// ---------------------------------------------------------------------------
// Shared maps
// ---------------------------------------------------------------------------

const AMENITY_MAP: Record<string, { icon: LucideIcon; label: string; color: string }> = {
  baby: { icon: Baby, label: 'Kids (0-4yrs)', color: '#E65E5B' },
  hand: { icon: Hand, label: 'ASL', color: '#39AEDA' },
  coffee: { icon: Coffee, label: 'Cafe', color: '#E65E5B' },
};

const BADGE_ICON_MAP: Record<string, LucideIcon> = {
  droplet: Droplet,
  'map-pin': MapPin,
  clock: Clock,
  'calendar-check': CalendarCheck,
};

// ---------------------------------------------------------------------------
// SermonRow — navigates to detail page on click
// ---------------------------------------------------------------------------

export function SermonRow({
  sermon,
  index,
  onSelect,
}: {
  sermon: Sermon;
  index: number;
  /** Called on click — parent is responsible for both selecting the sermon and navigating. */
  onSelect: () => void;
}) {
  const isCurrent = sermon.Is_Current;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative flex w-full items-center gap-3 px-4 py-3 text-left transition-all ${
        isCurrent ? 'text-white shadow-md' : 'hover:bg-black/5'
      }`}
      style={isCurrent ? { backgroundColor: '#39AEDA' } : undefined}
    >
      <div
        className="pointer-events-none absolute top-1/2 -left-1 -translate-y-1/2 opacity-[0.08] select-none"
        style={{ lineHeight: 1 }}
      >
        <span className="text-5xl font-black text-gray-900">{index}</span>
      </div>

      {sermon.Speaker_Image_URL ? (
        <div className="relative z-10 shrink-0">
          <Img
            src={sermon.Speaker_Image_URL}
            alt={sermon.Speaker ?? ''}
            className={`h-14 w-14 object-cover ${isCurrent ? '' : 'grayscale'}`}
            style={{ borderRadius: '50%' }}
          />
          {isCurrent && (
            <div
              className="absolute -right-0.5 -bottom-0.5 flex h-4 w-4 items-center justify-center bg-red-500"
              style={{ borderRadius: '50%' }}
            >
              <Radio className="h-2.5 w-2.5 text-white" />
            </div>
          )}
        </div>
      ) : (
        <div className="relative z-10 h-14 w-14 shrink-0" />
      )}

      <div className="relative z-10 min-w-0 flex-1">
        <p
          className={`text-[10px] font-semibold tracking-wide uppercase ${
            isCurrent ? 'text-white/80' : 'text-gray-600'
          }`}
        >
          {sermon.Date}
        </p>
        <p
          className={`text-sm leading-tight font-bold uppercase ${
            isCurrent ? 'text-white' : 'text-gray-900'
          }`}
        >
          {sermon.Title}
        </p>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          {sermon.Speaker && (
            <p
              className={`text-[10px] font-semibold tracking-wide uppercase ${
                isCurrent ? 'text-white/80' : 'text-gray-600'
              }`}
            >
              {sermon.Speaker}
            </p>
          )}
          {sermon.Badges.length > 0 && (
            <div className="flex items-center gap-2">
              {sermon.Badges.map((badge) => {
                const BIcon = BADGE_ICON_MAP[badge.Icon];
                return (
                  <span
                    key={badge.Label}
                    className="flex items-center gap-0.5 text-[10px] font-bold uppercase"
                    style={{ color: isCurrent ? 'white' : '#E65E5B' }}
                  >
                    {BIcon && <BIcon className="h-2.5 w-2.5" />}
                    {badge.Label}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// SermonDetailContent — full sermon detail (no back button)
// ---------------------------------------------------------------------------

export function SermonDetailContent({
  sermon,
  index,
  pageUrl,
}: {
  sermon: Sermon;
  index: number;
  pageUrl?: string | null;
}) {
  const isPast = sermon.Scripture != null || sermon.Big_Idea != null;

  return (
    <div className="relative space-y-5">
      <div
        className="pointer-events-none absolute top-2 right-4 opacity-[0.06] select-none"
        style={{ lineHeight: 1 }}
      >
        <span className="text-[140px] font-black text-gray-900 sm:text-[180px]">{index}</span>
      </div>

      <div className="relative">
        <h3 className="text-xl leading-tight font-black tracking-tight text-gray-900 uppercase sm:text-2xl">
          {sermon.Title}
        </h3>
      </div>

      {isPast ? (
        <>
          {sermon.Scripture && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <BookOpen className="h-4 w-4 shrink-0" style={{ color: '#E65E5B' }} />
                <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Scripture
                </p>
              </div>
              <a
                href={sermon.Scripture.URL}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-6 inline-flex items-center gap-1.5 text-sm font-bold text-gray-900 uppercase transition-colors hover:text-gray-600"
              >
                {sermon.Scripture.Reference}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          )}

          {sermon.Big_Idea && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 shrink-0" style={{ color: '#E65E5B' }} />
                <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Big Idea
                </p>
              </div>
              <p className="ml-6 text-sm leading-snug font-bold text-gray-900 uppercase">
                {sermon.Big_Idea}
              </p>
            </div>
          )}

          {sermon.Points && sermon.Points.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <List className="h-4 w-4 shrink-0" style={{ color: '#E65E5B' }} />
                <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Points
                </p>
              </div>
              <div className="ml-6 space-y-2">
                {sermon.Points.map((point, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-xs font-bold text-gray-600">{i + 1}.</span>
                    <div>
                      <p className="text-sm leading-snug font-bold text-gray-900 uppercase">
                        {point.Text}
                      </p>
                      <a
                        href={point.URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-bold text-gray-600 uppercase transition-colors hover:text-gray-900"
                      >
                        {point.Reference}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {sermon.Location && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0" style={{ color: '#E65E5B' }} />
                <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">Where</p>
              </div>
              <p className="ml-6 text-sm leading-snug font-bold whitespace-pre-line text-gray-900 uppercase">
                {sermon.Location}
              </p>
            </div>
          )}

          {sermon.Service_Times && sermon.Service_Times.length > 0 && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0" style={{ color: '#E65E5B' }} />
                <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">When</p>
              </div>
              <div className="ml-6 space-y-1.5">
                {sermon.Service_Times.map((st) => (
                  <div key={st.Time} className="flex items-center gap-2">
                    <p className="text-lg font-bold text-gray-900">{st.Time}</p>
                    <div className="h-px flex-1 bg-gray-300" />
                    <div className="flex items-center gap-1.5">
                      {st.Amenities.map((a) => {
                        const amenity = AMENITY_MAP[a];
                        if (!amenity) return null;
                        const AIcon = amenity.icon;
                        return (
                          <AIcon key={a} className="h-4 w-4" style={{ color: amenity.color }} />
                        );
                      })}
                    </div>
                  </div>
                ))}
                <AmenityLegend serviceTimes={sermon.Service_Times} />
              </div>
            </div>
          )}

          {sermon.Instructions && (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <Info className="h-4 w-4 shrink-0" style={{ color: '#E65E5B' }} />
                <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Instructions
                </p>
              </div>
              <p className="ml-6 text-sm leading-snug font-bold text-gray-900 uppercase">
                {sermon.Instructions}
              </p>
            </div>
          )}
        </>
      )}

      {(sermon.Notes_URL ||
        sermon.Watch_URL ||
        sermon.Badges.some((b) => b.Label === 'Baptisms')) && (
        <div className="space-y-2">
          {sermon.Notes_URL && (
            <a
              href={sermon.Notes_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
              style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: 'rgba(0,0,0,0.03)' }}
            >
              <div className="flex items-center gap-3">
                <NotebookPen className="h-5 w-5 shrink-0" style={{ color: '#E65E5B' }} />
                <div>
                  <p className="text-sm font-bold text-gray-900">Sermon Notes</p>
                  <p className="mt-0.5 text-xs text-gray-500">Follow along or review the message</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
            </a>
          )}
          {sermon.Watch_URL && (
            <a
              href={sermon.Watch_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
              style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: 'rgba(0,0,0,0.03)' }}
            >
              <div className="flex items-center gap-3">
                <Play className="h-5 w-5 shrink-0" style={{ color: '#373C46', fill: '#373C46' }} />
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {sermon.Is_Current ? 'Watch Live' : 'Watch'}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {sermon.Is_Current ? 'Stream the service now' : 'Watch the full message'}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
            </a>
          )}
          {sermon.Badges.length > 0 && sermon.Badges.some((b) => b.Label === 'Baptisms') && (
            <a
              href="#"
              className="group flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
              style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: 'rgba(0,0,0,0.03)' }}
            >
              <div className="flex items-center gap-3">
                <Droplet className="h-5 w-5 shrink-0" style={{ color: '#39AEDA' }} />
                <div>
                  <p className="text-sm font-bold text-gray-900">Get Baptized</p>
                  <p className="mt-0.5 text-xs text-gray-500">Take the next step in your faith</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
            </a>
          )}
        </div>
      )}

      {pageUrl && (
        <a
          href={pageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="-mx-5 mt-0 -mb-5 flex items-center justify-center gap-2 border-t border-black/10 py-4 text-sm font-semibold tracking-wide text-gray-900 uppercase transition-opacity hover:opacity-80"
        >
          Learn More
          <ChevronRight className="h-4 w-4" />
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SermonList — scrollable list with indicator
// ---------------------------------------------------------------------------

export function SermonList({
  sermons,
  isOpen,
  onSelectSermon,
  pageUrl,
}: {
  sermons: Sermon[];
  isOpen: boolean;
  onSelectSermon: (sermon: Sermon) => void;
  pageUrl?: string | null;
}) {
  const sermonListRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex min-h-[400px] flex-col">
      <div className="relative flex-1">
        <div
          ref={sermonListRef}
          className="max-h-[380px] space-y-2 overflow-y-auto p-4"
          style={{ scrollbarWidth: 'none' }}
        >
          {sermons.map((sermon, index) => (
            <SermonRow
              key={sermon.ID}
              sermon={sermon}
              index={index + 1}
              onSelect={() => onSelectSermon(sermon)}
            />
          ))}
        </div>
        <ScrollIndicator
          containerRef={sermonListRef}
          active={isOpen}
          condensed
          gradientClass="bg-transparent"
        />
      </div>

      {pageUrl && (
        <div className="border-t border-black/5 px-4 py-3">
          <a
            href={pageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 text-xs font-semibold tracking-wide text-gray-600 uppercase transition-colors hover:text-gray-900"
          >
            <BookOpenText className="h-3.5 w-3.5" />
            View All Series
          </a>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AmenityLegend
// ---------------------------------------------------------------------------

function AmenityLegend({ serviceTimes }: { serviceTimes: ServiceTime[] }) {
  const allAmenities = [...new Set(serviceTimes.flatMap((st) => st.Amenities))];
  if (allAmenities.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 pt-1">
      {allAmenities.map((a) => {
        const amenity = AMENITY_MAP[a];
        if (!amenity) return null;
        const AIcon = amenity.icon;
        return (
          <span
            key={a}
            className="flex items-center gap-1 text-[10px] font-semibold tracking-wide text-gray-500 uppercase"
          >
            <AIcon className="h-3 w-3" style={{ color: amenity.color }} />
            {amenity.label}
          </span>
        );
      })}
    </div>
  );
}
